<template>
  <div class="section">
    <div class="container">
      <div class="level">
        <div class="level-left">
          <div class="level-item buttons">
            <b-button type="is-info" icon-left="plus" @click="creatingReport = true">
              New
            </b-button>
            <b-button type="is-info" icon-left="plus" @click="verifReport = true">
              Stock validation
            </b-button>
            <b-button type="is-info" icon-left="plus" @click="createFifoReport = true">
              FIFO Report
            </b-button>
          </div>
          <div v-if="creatingReport" />
        </div>

        <div class="level-right">
          <div class="level-item">
            <b-button icon-left="refresh" :loading="isLoading" :disabled="isLoading" @click="() => refresh()">
              Refresh
            </b-button>
          </div>
        </div>
      </div>
      <div class="columns">
        <div class="column is-full">
          <b-loading v-model="isLoading" :is-full-page="false" />
          <ag-grid-vue
            style="width: 100%; height: 600px;"
            class="ag-theme-balham"
            :row-data="reports"
            :grid-options="gridOptions"
            :pagination="true"
            :pagination-auto-page-size="true"
            :multi-sort-key="'ctrl'"
            :modules="modules"
            row-selection="multiple"
          />
        </div>
      </div>

      <b-modal :active.sync="creatingReport" scroll="keep" has-modal-card>
        <ReportForm :reports="fullReports" :extended-reports="reports" :customers="customers" @submit="generateReport" />
      </b-modal>

      <b-modal :active.sync="createFifoReport" scroll="keep" has-modal-card>
        <div class="modal-card" style="min-height: 550px">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Create FIFO Report
            </p>
            <button class="delete" aria-label="close" @click="createFifoReport = false" />
          </header>
          <section class="modal-card-body">
            <b-field>
              <multiselect
                v-model="newFifoReport.soldTo"
                :options="customers"
                :multiple="false"
                :close-on-select="true"
                placeholder="Pick sold to..."
                :custom-label="(obj) => obj.id + ' - ' + obj.name"
                track-by="name"
                style="min-width: 400px;"
              />
            </b-field>
            <b-field>
              <b-datepicker
                v-model="newFifoReport.endDate"
                name="date"
                icon="calendar-alt"
                placeholder="End date"
                :date-formatter="(date) => $dayjs(date).format('DD/MM/YYYY')"
              />
            </b-field>
            <b-field>
              <b-radio v-model="newFifoReport.travelType" native-value="DMC">
                Domestic
              </b-radio>
              <b-radio v-model="newFifoReport.travelType" native-value="ORS">
                Oversea
              </b-radio>
              <b-radio v-model="newFifoReport.travelType" native-value="ALL">
                All
              </b-radio>
            </b-field>
            <b-field>
              <b-switch
                v-model="newFifoReport.typeSHI"
                type="is-primary"
                :true-value="1"
                :false-value="0"
              >
                {{ newFifoReport.typeSHI === 1 ? 'All shippings' : 'Direct shippings only' }}
              </b-switch>
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <div class="is-flex is-align-content-center ml-auto">
              <b-checkbox v-model="isTest" class="mr-5">
                Test
              </b-checkbox>
              <b-button :type="isTest? 'is-primary' : 'is-info'" :icon-left="isTest? 'vial' : 'play'" :disabled="!newFifoReport.soldTo || !newFifoReport.endDate" @click="generateFifoReport">
                Generate
              </b-button>
            </div>
          </footer>
        </div>
      </b-modal>

      <b-modal :active.sync="verifReport" scroll="keep" has-modal-card>
        <ReturnData :customers="customers" @submit="sendData" />
      </b-modal>
    </div>
  </div>
</template>

<script>
import { saveAs } from 'file-saver'

import Customer from '@/services/v2/customer'
import Report from '@/services/v2/report'
import ReportForm from './report-form'
import ReturnData from './return-data'
import { AllModules } from '@ag-grid-enterprise/all-modules'
import sortAndFilterMixins from '@/mixins/agGridSortAndFilter'
import { NotificationProgrammatic as Notification } from 'buefy'

export default {
  components: {
    ReportForm,
    ReturnData
  },
  mixins: [sortAndFilterMixins],
  data: function () {
    return {
      modules: AllModules,
      reports: [],
      fullReports: [],
      customers: [],
      creatingReport: false,
      verifReport: false,
      createFifoReport: false,
      isTest: false,
      newFifoReport: {
        soldTo: null,
        endDate: null,
        travelType: 'ALL',
        typeSHI: 0
      },
      isLoading: false,
      gridOptions: {
        masterDetail: true,
        getContextMenuItems: this.mainActions,

        defaultColDef: {
          floatingFilter: true,
          enableValue: true,
          enableRowGroup: true,
          enablePivot: true,
          sortable: true,
          filter: true,
          resizable: true

        },

        columnDefs: [
          {
            headerName: 'Report ID',
            field: 'id',
            sort: 'desc'
          },
          {
            headerName: 'Ship to',
            valueGetter: (params) => {
              if (params.data.customerCode === '-1' || params.data.customerCode === null) {
                return ''
              } else {
                return params.data.customerCode + ' - ' + this.customers.find(c => c.id === params.data.customerCode)?.name
              }
            }
          },
          {
            headerName: 'Sold to',
            valueGetter: (params) => {
              if (params.data.customerSoldTo === '-1' || params.data.customerSoldTo === null) {
                return ''
              } else {
                return params.data.customerSoldTo + ' - ' + this.customers.find(c => c.id === params.data.customerSoldTo)?.name
              }
            }
          },
          {
            headerName: 'Generation time',
            valueGetter: (params) => {
              return params.data && params.data.generationDate ? this.$dayjs(params.data.generationDate).format('DD/MM/YYYY') : ''
            },
            comparator: this.dateSort
          },
          {
            headerName: 'Period',
            valueGetter: (params) => {
              if (params.data.validate) return this.$dayjs(params.data.dateEnd, 'DDMMYYYY').format('DD/MM/YYYY')
              return params.data ? this.$dayjs(params.data.dateStart, 'DDMMYYYY').format('DD/MM/YYYY') + ' - ' + this.$dayjs(params.data.dateEnd, 'DDMMYYYY').format('DD/MM/YYYY') : ''
            },
            comparator: this.dateSort
          },
          {
            headerName: 'Stock validation',
            field: 'validate',
            cellRenderer: (params) => {
              return params.value ? '<i class="fa fa-check has-text-primary" />' : '<i class="fa fa-triangle-exclamation has-text-warning" />'
            },
            tooltipValueGetter: (params) => {
              return params.value ? '' : 'Please validate the most recent stock.'
            }
          },
          {
            headerName: 'Report',
            valueGetter: (params) => {
              if (!params.data.validate) return params.data.hasReport ? 'Ready to download' : 'Processing'
            }
          }
        ]
      }

    }
  },
  created: async function () {
    await this.loadData()
  },
  methods: {
    sendData: function (data) {
      Notification.open({
        message: 'Your file is being processed, this might take a few minutes...',
        type: 'is-info',
        closable: true,
        hasIcon: true,
        indefinite: false,
        autoClose: true,
        duration: 5000
      })
      Report.returnData(data)
      this.verifReport = false
      this.refresh()
    },
    async loadAllReports () {
      this.fullReports = await Report.getAll()
      this.reports = await Report.getRecentReport()
    },
    loadData: async function () {
      this.isLoading = true
      this.customers = await Customer.getAll({})
      await this.loadAllReports()
      this.isLoading = false
    },
    generateReport: async function (data) {
      this.$buefy.notification.open({
        message: 'Your file is being processed, this might take a few minutes...',
        type: 'is-info',
        closable: true,
        hasIcon: true,
        indefinite: false,
        autoClose: true,
        duration: 5000
      })
      Report.generateReport(data)
      this.refresh()
    },

    async generateFifoReport () {
      if (!this.newFifoReport.soldTo || !this.newFifoReport.endDate) {
        this.$buefy.toast.open({ message: 'Please select a sold to and end date.', type: 'is-danger', duration: 4000 })
      }

      if (!this.newFifoReport.travelType || !this.newFifoReport.typeSHI) {
        this.$buefy.toast.open({ message: 'Travel type and shipping type can not be empty.', type: 'is-danger', duration: 4000 })
      }
      try {
        Report.generateReport({
          soldTo: this.newFifoReport.soldTo.id,
          endDate: this.$dayjs(this.newFifoReport.endDate).format('DDMMYYYY'),
          travelType: this.newFifoReport.travelType,
          typeSHI: this.newFifoReport.typeSHI,
          isFIFO: true,
          isTest: this.isTest
        })
        this.$buefy.notification.open({
          message: 'Your file is being processed, this might take a few minutes...',
          type: 'is-info',
          closable: true,
          hasIcon: true,
          indefinite: false,
          autoClose: true,
          duration: 5000
        })
      } catch (e) {
        this.$buefy.toast.open({ message: e.message || e, type: 'is-danger', duration: 4000 })
      }
      this.newFifoReport = {
        soldTo: null,
        endDate: null,
        travelType: 'ALL',
        typeSHI: 0
      }
      this.createFifoReport = false
      this.refresh()
    },

    mainActions (params) {
      if (params.node.data.hasReport) {
        return [
          {
            name: 'Get report',
            icon: '<span class="ag-icon ag-icon-save" unselectable="on"></span>',
            action: () => {
              this.getReport(params.node.data)
            }
          }
        ]
      }
    },

    getReport: async function (data) {
      try {
        const query = {
          customerCode: data.customerCode,
          customerSoldTo: data.customerSoldTo,
          endDate: data.dateEnd
        }
        const result = await Report.getReport(data.id, query)
        saveAs(result.data, 'Report_' + data.customerCode + '_' + data.customerSoldTo + '_' + data.dateEnd + '.zip')
      } catch (e) {
        this.$buefy.toast.open({ message: e.message || e, type: 'is-danger', duration: 4000 })
      }
    },

    hasReport: async function (customerCode, customerSoldTo, dateEnd) {
      try {
        const result = await Report.hasReport(customerCode, customerSoldTo, dateEnd)
        return result
      } catch (e) {
        this.$buefy.toast.open({ message: e.message || e, type: 'is-danger', duration: 4000 })
      }
    },

    async refresh () {
      this.isLoading = true
      this.reports = []
      await this.loadAllReports()
      this.gridOptions.api.refreshCells()
      this.isLoading = false
    }

  }
}
</script>
